import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';

import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import Footer from '../landing-page/components/Footer';
import ClientDashboardNavbar from './components/ClientDashboardNavbar';
import { Wrench, Leaf, Truck, Droplets, PaintBucket, HardHat, Building2, ChevronLeft, ChevronRight, FileText, Plug, Snowflake, SprayCan, Star } from 'lucide-react';

const SERVICE_CATEGORIES = {
  'Pulizie': ['Pulizie domestiche', 'Impresa di pulizia', 'Pulizia moquette e tappeti', 'Sgombero ingombranti'],
  'Tuttofare': ['Tuttofare', 'Montaggio mobili', 'Riparazione elettrodomestici', 'Installazione e riparazione serrature'],
  'Giardinieri': ['Giardiniere', 'Taglio e cura del prato', 'Potatura e abbattimento alberi', 'Impianti di irrigazione'],
  'Traslochi': ['Ditta traslochi', 'Imballaggio e disimballaggio', 'Spostamento mobili pesanti', 'Trasporto pianoforti'],
  'Idraulici': ['Idraulico / Termoidraulico', 'Riparazione scaldabagno', 'Riparazione tubature', 'Spurgo e fognature'],
  'Elettricisti': ['Elettricista', 'Installazione prese e interruttori', 'Impianto domotica', 'Impianto fotovoltaico'],
  'Imbianchini': ['Imbianchino', 'Tappezziere', 'Tende e oscuranti', 'Verniciatura recinzioni'],
  'Climatizzazione': ['Climatizzazione', 'Riparazione caldaia', 'Pulizia condotti e ventilazione', 'Pulizia e riparazione camino'],
  'Edilizia': ['Ristrutturazioni', 'Muratore', 'Cartongessista', 'Riparazione tetti'],
  'Ascensoristi': ['Ascensoristi', 'Manutenzione ascensori', 'Installazione piattaforme elevatrici', 'Riparazione e collaudo ascensori'],
};

const TAB_ICONS = {
  'Pulizie': SprayCan,
  'Tuttofare': Wrench,
  'Giardinieri': Leaf,
  'Traslochi': Truck,
  'Idraulici': Droplets,
  'Elettricisti': Plug,
  'Imbianchini': PaintBucket,
  'Climatizzazione': Snowflake,
  'Edilizia': HardHat,
  'Ascensoristi': Building2,
};

const TAB_LABELS = Object.keys(SERVICE_CATEGORIES);

const PULIZIE_IMAGES = {
  'Pulizie domestiche': '/assets/images/pulizie_domestiche.png',
  'Impresa di pulizia': '/assets/images/impresa_pulizia.png',
  'Pulizia moquette e tappeti': '/assets/images/pulizia_moquette_tappeti.png',
  'Sgombero ingombranti': '/assets/images/sgombero_ingombranti.png',
};

const TUTTOFARE_IMAGES = {
  'Tuttofare': '/assets/images/tuttofare_wrench_hinge.png',
  'Montaggio mobili': '/assets/images/montaggio_mobili.png',
  'Riparazione elettrodomestici': '/assets/images/riparazione_elettrodomestici.png',
  'Installazione e riparazione serrature': '/assets/images/installazione_riparazione_serrature.png',
};

const GIARDINIERI_IMAGES = {
  'Giardiniere': '/assets/images/giardiniere_pruning.png',
  'Taglio e cura del prato': '/assets/images/taglio_cura_prato.png',
  'Potatura e abbattimento alberi': '/assets/images/potatura_abbattimento_alberi.png',
  'Impianti di irrigazione': '/assets/images/impianti_irrigazione.png',
};

const TRASLOCHI_IMAGES = {
  'Ditta traslochi': '/assets/images/traslochi_ditta_traslochi.png',
  'Imballaggio e disimballaggio': '/assets/images/traslochi_imballaggio_disimballaggio.png',
  'Spostamento mobili pesanti': '/assets/images/traslochi_spostamento_mobili_pesanti.png',
  'Trasporto pianoforti': '/assets/images/traslochi_trasporto_pianoforti.png',
};

const IDRAULICI_IMAGES = {
  'Idraulico / Termoidraulico': '/assets/images/idraulico_termoidraulico.png',
  'Riparazione scaldabagno': '/assets/images/riparazione_scaldabagno.png',
  'Riparazione tubature': '/assets/images/riparazione_tubature.png',
  'Spurgo e fognature': '/assets/images/spurgo_fognature.png',
};

const ELETTRICISTI_IMAGES = {
  'Elettricista': '/assets/images/elettricista_voltage_tester.png',
  'Installazione prese e interruttori': '/assets/images/installazione_prese_interruttori.png',
  'Impianto domotica': '/assets/images/impianto_domotica_smart_wiring.png',
  'Impianto fotovoltaico': '/assets/images/impianto_fotovoltaico_solar_panel.png',
};

const IMBIANCHINI_IMAGES = {
  'Imbianchino': '/assets/images/imbianchino_paint_roller.png',
  'Tappezziere': '/assets/images/tappezziere_wallpaper_smoothing.png',
  'Tende e oscuranti': '/assets/images/tende_oscuranti_curtain_rail.png',
  'Verniciatura recinzioni': '/assets/images/verniciatura_recinzioni_fence_brush.png',
};

const CLIMATIZZAZIONE_IMAGES = {
  'Climatizzazione': '/assets/images/climatizzazione_ac_installation.png',
  'Riparazione caldaia': '/assets/images/riparazione_caldaia_boiler_wrench.png',
  'Pulizia condotti e ventilazione': '/assets/images/pulizia_condotte_ventilazione_vent_brush.png',
  'Pulizia e riparazione camino': '/assets/images/pulizia_riparazione_camino_chimney_brush.png',
};

const EDILIZIA_IMAGES = {
  'Ristrutturazioni': '/assets/images/ristrutturazioni_blueprints_renovation_v2.png',
  'Muratore': '/assets/images/muratore_brick_mortar_trowel_v2.png',
  'Cartongessista': '/assets/images/cartongessista_drywall_screw_metal_stud_v2.png',
  'Riparazione tetti': '/assets/images/riparazione_tetti_roof_tiles_gloves_v2.png',
};

const ASCENSORISTI_IMAGES = {
  'Ascensoristi': '/assets/images/ascensoristi_glass_elevator_shaft_building.png',
  'Manutenzione ascensori': '/assets/images/manutenzione_ascensori_control_panel_gloves.png',
  'Installazione piattaforme elevatrici': '/assets/images/installazione_piattaforme_elevatrici_home_elevator_glass.png',
  'Riparazione e collaudo ascensori': '/assets/images/riparazione_collaudo_ascensori_multimeter_machinery.png',
};

const SUBCATEGORY_SUBTITLES = {
  'Pulizie domestiche': 'Preventivo gratuito',
  'Impresa di pulizia': 'Risposta in 24h',
  'Pulizia moquette e tappeti': 'Professionisti verificati',
  'Sgombero ingombranti': 'Intervento rapido',
  'Tuttofare': 'Disponibile subito',
  'Montaggio mobili': 'Preventivo gratuito',
  'Riparazione elettrodomestici': 'Intervento rapido',
  'Installazione e riparazione serrature': 'Pronto intervento',
  'Giardiniere': 'Preventivo gratuito',
  'Taglio e cura del prato': 'Risultato garantito',
  'Potatura e abbattimento alberi': 'Professionisti esperti',
  'Impianti di irrigazione': 'Installazione rapida',
  'Ditta traslochi': 'Preventivo gratuito',
  'Imballaggio e disimballaggio': 'Cura totale',
  'Spostamento mobili pesanti': 'Zero danni',
  'Trasporto pianoforti': 'Massima cura',
  'Idraulico / Termoidraulico': 'Pronto intervento',
  'Riparazione scaldabagno': 'Intervento rapido',
  'Riparazione tubature': 'Emergenze 24h',
  'Spurgo e fognature': 'Preventivo gratuito',
  'Elettricista': 'Certificato e garantito',
  'Installazione prese e interruttori': 'Preventivo gratuito',
  'Impianto domotica': 'Casa intelligente',
  'Impianto fotovoltaico': 'Risparmio garantito',
  'Imbianchino': 'Preventivo gratuito',
  'Tappezziere': 'Finitura perfetta',
  'Tende e oscuranti': 'Su misura',
  'Verniciatura recinzioni': 'Durata garantita',
  'Climatizzazione': 'Installazione rapida',
  'Riparazione caldaia': 'Pronto intervento',
  'Pulizia condotti e ventilazione': 'Aria pulita',
  'Pulizia e riparazione camino': 'Sicurezza garantita',
  'Ristrutturazioni': 'Preventivo gratuito',
  'Muratore': 'Esperienza certificata',
  'Cartongessista': 'Finitura perfetta',
  'Riparazione tetti': 'Intervento rapido',
  'Ascensoristi': 'Certificati e garantiti',
  'Manutenzione ascensori': 'Contratto incluso',
  'Installazione piattaforme elevatrici': 'Bonus 75%',
  'Riparazione e collaudo ascensori': 'Intervento rapido',
};

const ClientDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [professions, setProfessions] = useState([]);
  const [filteredProfessions, setFilteredProfessions] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedProfession, setSelectedProfession] = useState(null);
  const [activeRequests, setActiveRequests] = useState([]);
  const [archivedRequests, setArchivedRequests] = useState([]);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [professionsError, setProfessionsError] = useState(null);
  const [activeTab, setActiveTab] = useState('Pulizie');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const tabScrollRef = useRef(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [createDropdownSearch, setCreateDropdownSearch] = useState('');
  const createDropdownRef = useRef(null);

  const checkScrollState = () => {
    const el = tabScrollRef?.current;
    if (!el) return;
    setCanScrollLeft(el?.scrollLeft > 4);
    setCanScrollRight(el?.scrollLeft + el?.clientWidth < el?.scrollWidth - 4);
  };

  const handleTabPrev = () => {
    const el = tabScrollRef?.current;
    if (el) {
      el?.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const handleTabNext = () => {
    const el = tabScrollRef?.current;
    if (el) {
      el?.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Fetch professions from Supabase
  useEffect(() => {
    const fetchProfessions = async () => {
      try {
        // Check if supabase client is initialized
        if (!supabase) {
          console.error('Supabase client not initialized');
          setProfessionsError('Servizio non disponibile. Riprova più tardi.');
          return;
        }

        const { data, error } = await supabase?.from('professions')?.select('id, name, category')?.order('name', { ascending: true });

        if (error) {
          console.error('Error fetching professions:', error);
          setProfessionsError('Impossibile caricare le professioni. Riprova più tardi.');
          return;
        }

        if (data && data?.length > 0) {
          setProfessions(data);
          setProfessionsError(null);
          if (location?.state?.selectedProfession) {
            startNuovaRichiesta(location?.state?.selectedProfession);
            navigate('/cliente/dashboard', { replace: true, state: null });
          }
        } else {
          console.warn('No professions found in database');
          setProfessionsError('Nessuna professione disponibile.');
        }
      } catch (err) {
        console.error('Failed to fetch professions:', err);
        setProfessionsError('Errore di connessione. Verifica la tua connessione internet.');
      }
    };

    fetchProfessions();
  }, []);

  // Filter professions based on search query
  useEffect(() => {
    if (searchQuery?.trim() === '') {
      setFilteredProfessions([]);
      setShowAutocomplete(false);
      return;
    }

    const query = searchQuery?.toLowerCase();
    const filtered = professions?.filter(
      (profession) =>
        profession?.name?.toLowerCase()?.includes(query) ||
        profession?.category?.toLowerCase()?.includes(query)
    );

    setFilteredProfessions(filtered);
    setShowAutocomplete(filtered?.length > 0);
  }, [searchQuery, professions]);

  // Check authentication and role authorization
  useEffect(() => {
    const checkAccess = async () => {
      if (authLoading) return;

      if (!user) {
        navigate('/login-form?returnTo=/cliente/dashboard');
        return;
      }

      try {
        // Verify email confirmation
        const { data: { user: currentUser }, error: userError } = await supabase?.auth?.getUser();
        
        if (userError || !currentUser) {
          navigate('/login-form');
          return;
        }

        // Check if email is confirmed
        if (!currentUser?.email_confirmed_at) {
          setError('Devi confermare la tua email prima di accedere alla dashboard.');
          setLoading(false);
          return;
        }

        // Check user role from user_metadata
        const role = currentUser?.user_metadata?.role;
        
        if (!isAdmin && role !== 'cliente') {
          // User is not a client - redirect to appropriate page
          console.log('❌ Accesso negato: utente non è un cliente');
          await supabase?.auth?.signOut();
          navigate('/login-form');
          return;
        }

        setUserRole(role);
        setLoading(false);
      } catch (err) {
        console.error('Access check error:', err);
        setError('Errore durante la verifica dell\'accesso');
        setLoading(false);
      }
    };

    checkAccess();
  }, [user, authLoading, navigate]);

  const handleSearch = (e) => {
    e?.preventDefault();
    // Search functionality placeholder
    console.log('Search query:', searchQuery);
    if (selectedProfession) {
      startNuovaRichiesta(selectedProfession);
    }
  };

  const handleProfessionSelect = (profession) => {
    setSearchQuery(profession?.name);
    setSelectedProfession(profession);
    setShowAutocomplete(false);
    startNuovaRichiesta(profession);
  };

  const startNuovaRichiesta = (profession) => {
    console.log('Starting Nuova Richiesta flow with service category:', profession);
    // Navigate to service request creation page with selected category
    navigate('/cliente/richiesta/nuova', { state: { selectedProfession: profession } });
  };

  // Fetch client's active service requests
  useEffect(() => {
    const fetchActiveRequests = async () => {
      if (!user || userRole !== 'cliente') return;

      setLoadingRequest(true);
      const { data, error } = await supabase?.from('service_requests')?.select('*')?.eq('client_id', user?.id)?.eq('status', 'active')?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching active requests:', error);
      } else {
        setActiveRequests(data || []);
      }

      setLoadingRequest(false);
    };

    const fetchArchivedRequests = async () => {
      if (!user || userRole !== 'cliente') return;

      const { data, error } = await supabase
        ?.from('service_requests')
        ?.select('*')
        ?.eq('client_id', user?.id)
        ?.in('status', ['completed', 'archived', 'cancelled'])
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching archived requests:', error);
      } else {
        setArchivedRequests(data || []);
      }
    };

    const fetchPendingReviews = async () => {
      if (!user || userRole !== 'cliente') return;

      const { data, error } = await supabase?.from('review_invitations')?.select('token, service_request_id, service_requests(title), professional_id, users!review_invitations_professional_id_fkey(full_name)')?.eq('client_id', user?.id)?.is('used_at', null)?.gt('expires_at', new Date()?.toISOString())?.order('created_at', { ascending: false });

      if (!error && data) {
        setPendingReviews(data);
      }
    };

    fetchActiveRequests();
    fetchArchivedRequests();
    fetchPendingReviews();
  }, [user, userRole]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('it-IT', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent':
        return 'text-red-600 bg-red-50';
      case 'high':
        return 'text-orange-600 bg-orange-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'low':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Attiva',
      pending: 'In attesa',
      accepted: 'Accettata',
      in_progress: 'In corso',
      completed: 'Completata',
      cancelled: 'Annullata'
    };
    return labels?.[status] || status;
  };

  const getUrgencyLabel = (urgency) => {
    const labels = {
      urgent: 'Urgente',
      high: 'Alta',
      medium: 'Media',
      low: 'Bassa'
    };
    return labels?.[urgency] || urgency;
  };

  // Close create dropdown on outside click
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (createDropdownRef?.current && !createDropdownRef?.current?.contains(e?.target)) {
        setShowCreateDropdown(false);
        setCreateDropdownSearch('');
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Caricamento dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ClientDashboardNavbar
          selectedAvatar={null}
          avatarIcons={[]}
          overrideUserName={null}
        />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <div className="card max-w-md text-center">
            <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-4">Accesso Negato</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button
              variant="default"
              onClick={() => navigate('/login-form')}
              iconName="ArrowLeft"
            >
              Torna al Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <ClientDashboardNavbar
        selectedAvatar={null}
        avatarIcons={[]}
        overrideUserName={null}
      />
      {/* Hero Section with Search Bar */}
      <div 
        className="relative bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(/assets/images/client_dashboard_hero_interior.png)',
          minHeight: '320px',
          backgroundPosition: 'center center',
          backgroundSize: 'cover'
        }}
      >
        {/* Overlay for better text contrast */}
        <div className="absolute inset-0 bg-black/50"></div>
        
        {/* Centered Search Bar */}
        <div className="relative z-10 flex items-center justify-center min-h-[320px] px-4 py-8">
          <div className="w-full max-w-3xl relative">
            <p className="text-white text-lg font-semibold mb-3">Di cosa hai bisogno?</p>
            <form onSubmit={handleSearch} className="flex items-stretch gap-0 shadow-2xl">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  onFocus={() => {
                    if (filteredProfessions?.length > 0) {
                      setShowAutocomplete(true);
                    }
                  }}
                  placeholder="Es. elettricista, idraulico, giardiniere..."
                  className="w-full px-6 py-4 text-lg border-none rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary h-14"
                  autoComplete="off"
                  disabled={professionsError !== null}
                />
                {/* Autocomplete Dropdown */}
                {showAutocomplete && filteredProfessions?.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-lg shadow-xl max-h-80 overflow-y-auto z-50 mt-0">
                    {filteredProfessions?.map((profession) => (
                      <div
                        key={profession?.id}
                        onClick={() => handleProfessionSelect(profession)}
                        className="px-6 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="font-semibold text-foreground">{profession?.name}</div>
                        {profession?.category && (
                          <div className="text-sm text-muted-foreground">{profession?.category}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Error Message */}
                {professionsError && (
                  <div className="absolute top-full left-0 right-0 bg-red-50 border border-red-200 rounded-b-lg shadow-lg p-4 mt-0 z-50">
                    <div className="flex items-start gap-3">
                      <Icon name="AlertCircle" size={20} color="#dc2626" className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">{professionsError}</p>
                        <p className="text-xs text-red-600 mt-1">Verifica la tua connessione internet e ricarica la pagina.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button
                type="submit"
                className="px-8 bg-primary text-white font-semibold rounded-r-lg hover:bg-primary/90 transition-colors h-14 flex items-center justify-center"
              >
                Cerca
              </button>
            </form>
          </div>
        </div>
      </div>
      {/* Le Tue Richieste Section */}
      <div className="flex-1 container mx-auto px-4 py-8">

        {/* Recensioni da lasciare section */}
        {pendingReviews?.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 pl-3" style={{ borderLeft: '4px solid #fbbf24' }}>
              <Star size={20} color="#fbbf24" fill="#fbbf24" />
              <h3 className="text-lg font-semibold text-foreground">Recensioni da lasciare</h3>
            </div>
            <div className="flex flex-col gap-3">
              {pendingReviews?.map((inv) => (
                <div
                  key={inv?.token}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex items-center justify-between gap-4 flex-wrap"
                >
                  <div className="flex items-start gap-3">
                    <Star size={20} color="#fbbf24" fill="#fbbf24" className="flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        Lavoro: {inv?.service_requests?.title || '—'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Con: {inv?.users?.full_name || 'Professionista'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/review/${inv?.token}`)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '7px 16px',
                      borderRadius: '8px',
                      border: '1.5px solid #2780b5',
                      backgroundColor: 'transparent',
                      color: '#2780b5',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    Lascia recensione →
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-6">Le Tue Richieste</h2>
          
          {/* Active Requests */}
          <div className="mb-8">
            {/* Section header with blue left border accent and document icon */}
            <div className="flex items-center gap-3 mb-4 pl-3" style={{ borderLeft: '4px solid #32b3e5' }}>
              <FileText size={20} color="#32b3e5" />
              <h3 className="text-lg font-semibold text-foreground">Richieste Attive</h3>
            </div>
            
            {loadingRequest ? (
              <div className="card text-center py-12">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Caricamento...</p>
              </div>
            ) : activeRequests?.length > 0 ? (
              <div className="flex flex-col gap-4">
                {activeRequests?.map((request) => (
                  <div
                    key={request?.id}
                    className="card cursor-pointer transition-shadow duration-200 hover:shadow-lg"
                    style={{ borderTop: '3px solid #32b3e5' }}
                    onClick={() => navigate(`/cliente/richieste/${request?.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e?.key === 'Enter' || e?.key === ' ') {
                        navigate(`/cliente/richieste/${request?.id}`);
                      }
                    }}
                  >
                    <div className="mb-4">
                      <h4 className="text-xl font-bold text-foreground mb-2">{request?.title}</h4>
                      <p className="text-muted-foreground">{request?.description}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Stato</p>
                        <div className="flex items-center gap-2">
                          {request?.status === 'active' && (
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#32b3e5' }}></span>
                          )}
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700">
                            {getStatusLabel(request?.status)}
                          </span>
                        </div>
                      </div>

                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Urgenza</p>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getUrgencyColor(request?.urgency)}`}>
                            {getUrgencyLabel(request?.urgency)}
                          </span>
                        </div>
                      </div>

                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Data creazione</p>
                        <p className="text-foreground font-medium">{formatDate(request?.created_at)}</p>
                      </div>
                    </div>

                    {request?.location_name && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-muted-foreground mb-1">Località</p>
                        <div className="flex items-center gap-2">
                          <Icon name="MapPin" size={16} color="var(--color-muted-foreground)" />
                          <p className="text-foreground">{request?.location_name}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="card text-center py-12">
                <Icon name="Inbox" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-foreground mb-2">Nessuna richiesta attiva</h4>
                <p className="text-muted-foreground mb-6">
                  Non hai ancora creato nessuna richiesta di servizio
                </p>
                <div className="relative" ref={createDropdownRef}>
                  <button
                    onClick={() => { setShowCreateDropdown((v) => !v); setCreateDropdownSearch(''); }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Icon name="Plus" size={16} />
                    Crea nuova richiesta
                  </button>
                  {showCreateDropdown && (
                    <div className="absolute top-full mt-2 left-0 w-80 max-w-full bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-80 overflow-y-auto">
                      <div className="p-3 border-b border-gray-100 sticky top-0 bg-white z-10">
                        <input
                          type="text"
                          autoFocus
                          placeholder="Cerca un servizio..."
                          value={createDropdownSearch}
                          onChange={(e) => setCreateDropdownSearch(e?.target?.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </div>
                      <div>
                        {professions
                          ?.filter((p) => {
                            const q = createDropdownSearch?.toLowerCase()?.trim();
                            if (!q) return true;
                            return (
                              p?.name?.toLowerCase()?.includes(q) ||
                              p?.category?.toLowerCase()?.includes(q)
                            );
                          })
                          ?.map((profession) => (
                            <div
                              key={profession?.id}
                              onClick={() => {
                                setShowCreateDropdown(false);
                                setCreateDropdownSearch('');
                                navigate('/cliente/richiesta/nuova', { state: { selectedProfession: profession } });
                              }}
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                            >
                              <div className="font-medium text-sm text-foreground">{profession?.name}</div>
                              {profession?.category && (
                                <div className="text-xs text-muted-foreground mt-0.5">{profession?.category}</div>
                              )}
                            </div>
                          ))}
                        {professions?.filter((p) => {
                          const q = createDropdownSearch?.toLowerCase()?.trim();
                          if (!q) return true;
                          return (
                            p?.name?.toLowerCase()?.includes(q) ||
                            p?.category?.toLowerCase()?.includes(q)
                          );
                        })?.length === 0 && (
                          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                            Nessun servizio trovato
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Link to archived requests */}
          <div className="text-right">
            <button
              onClick={() => navigate('/cliente/richieste/archiviate')}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Visualizza richieste archiviate →
            </button>
          </div>
        </div>
      </div>
      {/* Service Category Section */}
      <div className="w-full px-4 md:px-8 lg:px-16 py-12">

        {/* Tab Bar — centered horizontally */}
        <div className="w-full flex justify-center">
          <div className="relative border-b border-gray-200 max-w-7xl w-full">
            {/* Left Arrow — absolutely positioned at left edge */}
            {canScrollLeft && (
              <button
                onClick={handleTabPrev}
                aria-label="Scorri a sinistra"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center bg-white border border-gray-200 rounded-full cursor-pointer"
                style={{
                  width: '32px',
                  height: '32px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <ChevronLeft size={16} color="#6b7280" />
              </button>
            )}

            {/* Tabs scrollable row — padded to avoid overlap with arrows */}
            <div
              ref={tabScrollRef}
              className="flex overflow-x-auto touch-pan-x md:justify-center"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
                paddingLeft: canScrollLeft ? '40px' : '0px',
                paddingRight: canScrollRight ? '40px' : '0px',
                transition: 'padding 0.2s',
              }}
              onScroll={checkScrollState}
            >
              {TAB_LABELS?.map((label) => {
                const TabIcon = TAB_ICONS?.[label];
                const isActive = activeTab === label;
                return (
                  <button
                    key={label}
                    onClick={() => setActiveTab(label)}
                    className="flex flex-col items-center flex-shrink-0 bg-white border-none cursor-pointer transition-colors"
                    style={{
                      padding: '12px 20px',
                      borderBottom: isActive ? '2px solid #2780b5' : '2px solid transparent',
                      marginBottom: '-1px',
                    }}
                  >
                    <TabIcon
                      size={24}
                      color={isActive ? '#2780b5' : '#9ca3af'}
                      style={{ marginBottom: '4px' }}
                    />
                    <span
                      className="text-sm font-medium whitespace-nowrap"
                      style={{ color: isActive ? '#2780b5' : '#6b7280' }}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Right Arrow — absolutely positioned at right edge */}
            {canScrollRight && (
              <button
                onClick={handleTabNext}
                aria-label="Scorri a destra"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center bg-white border border-gray-200 rounded-full cursor-pointer"
                style={{
                  width: '32px',
                  height: '32px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <ChevronRight size={16} color="#6b7280" />
              </button>
            )}
          </div>
        </div>
        {/* Card Grid */}
        <div style={{ padding: '24px 0' }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{ width: '100%' }}>
            {SERVICE_CATEGORIES?.[activeTab]?.map((subcategory) => {
              const pulizieSrc = activeTab === 'Pulizie' ? PULIZIE_IMAGES?.[subcategory] : null;
              const tuttofaresSrc = activeTab === 'Tuttofare' ? TUTTOFARE_IMAGES?.[subcategory] : null;
              const giardiniereSrc = activeTab === 'Giardinieri' ? GIARDINIERI_IMAGES?.[subcategory] : null;
              const traslochiSrc = activeTab === 'Traslochi' ? TRASLOCHI_IMAGES?.[subcategory] : null;
              const idrauliciSrc = activeTab === 'Idraulici' ? IDRAULICI_IMAGES?.[subcategory] : null;
              const elettricistiSrc = activeTab === 'Elettricisti' ? ELETTRICISTI_IMAGES?.[subcategory] : null;
              const imbianchiniSrc = activeTab === 'Imbianchini' ? IMBIANCHINI_IMAGES?.[subcategory] : null;
              const climatizzazioneSrc = activeTab === 'Climatizzazione' ? CLIMATIZZAZIONE_IMAGES?.[subcategory] : null;
              const ediliziasSrc = activeTab === 'Edilizia' ? EDILIZIA_IMAGES?.[subcategory] : null;
              const ascensoristiSrc = activeTab === 'Ascensoristi' ? ASCENSORISTI_IMAGES?.[subcategory] : null;
              const cardImageSrc = pulizieSrc || tuttofaresSrc || giardiniereSrc || traslochiSrc || idrauliciSrc || elettricistiSrc || imbianchiniSrc || climatizzazioneSrc || ediliziasSrc || ascensoristiSrc;
              return (
                <div
                  key={subcategory}
                  className="relative cursor-pointer overflow-hidden"
                  style={{ borderRadius: '16px', height: '220px' }}
                  onClick={() => startNuovaRichiesta({ name: subcategory })}
                >
                  {/* Background: image for Pulizie/Tuttofare, solid color otherwise */}
                  {cardImageSrc ? (
                    <img
                      src={cardImageSrc}
                      alt={subcategory}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                  <div
                    className="absolute inset-0"
                    style={{ backgroundColor: '#d1d5db' }}
                  />
                  )}
                  {/* Dark gradient overlay at bottom 40% */}
                  <div
                    className="absolute inset-x-0 bottom-0"
                    style={{
                      height: '40%',
                      background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.7))',
                    }}
                  />
                  {/* Card label */}
                  <div
                    className="absolute bottom-0 left-0"
                    style={{ padding: '16px' }}
                  >
                    <span
                      className="text-white"
                      style={{ fontWeight: 700, fontSize: '16px', display: 'block' }}
                    >
                      {subcategory}
                    </span>
                    {SUBCATEGORY_SUBTITLES?.[subcategory] && (
                      <span
                        style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', display: 'block', marginTop: '2px' }}
                      >
                        {SUBCATEGORY_SUBTITLES?.[subcategory]}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ClientDashboard;